import {
  CheckCircle2,
  ExternalLink,
  Globe,
  Key,
  Mail,
  PlayCircle,
  Send,
  ShieldCheck,
  ShoppingBag,
  XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../api/client.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { Field, Input, Select, Textarea } from '../components/Input.js';
import { PageHeader } from '../components/PageHeader.js';

type Settings = Record<string, unknown>;

function ProviderSection({
  id,
  name,
  href,
  keyPlaceholder,
  keyName,
  modelName,
  modelDefault,
  data,
  update,
  keyPresent,
  isActive,
  onKeyTyped,
}: {
  id: string;
  name: string;
  href: string;
  keyPlaceholder: string;
  keyName: string;
  modelName: string | null;
  modelDefault?: string;
  data: Settings;
  update: (k: string, v: unknown) => void;
  keyPresent: boolean;
  isActive: boolean;
  onKeyTyped: () => void;
}): JSX.Element {
  const s = (k: string): string => (data[k] === null ? '' : String(data[k] ?? ''));
  return (
    <div
      style={{
        padding: 14,
        border: '1px solid var(--c-border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--c-card)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 'var(--text-md)' }}>{name}</span>
          {isActive && (
            <span
              style={{
                fontSize: 'var(--text-xs)',
                background: 'var(--c-success-bg)',
                color: 'var(--c-success)',
                padding: '2px 8px',
                borderRadius: 99,
                fontWeight: 600,
              }}
            >
              Active
            </span>
          )}
          {keyPresent && !isActive && (
            <span
              style={{
                fontSize: 'var(--text-xs)',
                background: 'var(--c-divider)',
                color: 'var(--c-muted)',
                padding: '2px 8px',
                borderRadius: 99,
                fontWeight: 500,
              }}
            >
              Key saved
            </span>
          )}
        </div>
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          style={{
            fontSize: 'var(--text-base)',
            color: 'var(--c-primary)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          Get a key
          <ExternalLink size={12} />
        </a>
      </div>
      <Field label="API key" hint="leave as ••• to keep current" htmlFor={`${id}-key`}>
        <Input
          id={`${id}-key`}
          type="password"
          value={s(keyName)}
          onChange={(e) => {
            update(keyName, e.target.value);
            if (e.target.value.length > 0) onKeyTyped();
          }}
          placeholder={keyPlaceholder}
        />
      </Field>
      {modelName && (
        <Field label="Model" hint="leave blank to use the default" htmlFor={`${id}-model`}>
          <Input
            id={`${id}-model`}
            value={s(modelName)}
            onChange={(e) => update(modelName, e.target.value)}
            placeholder={modelDefault ?? ''}
          />
        </Field>
      )}
    </div>
  );
}

export function AdminSettingsPage(): JSX.Element {
  const [data, setData] = useState<Settings | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedSection, setSavedSection] = useState<string | null>(null);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [aiStatus, setAiStatus] = useState<{
    activeProvider: string | null;
    activeModel: string | null;
    keysPresent: Record<string, boolean>;
    explicitChoice: string | null;
  } | null>(null);
  const [aiTestLoading, setAiTestLoading] = useState(false);
  const [aiTestResult, setAiTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const loadAIStatus = (): void => {
    apiGet<{
      activeProvider: string | null;
      activeModel: string | null;
      keysPresent: Record<string, boolean>;
      explicitChoice: string | null;
    }>('/api/admin/ai/status')
      .then(setAiStatus)
      .catch(() => {
        /* fail quietly — the page still renders */
      });
  };
  useEffect(() => {
    loadAIStatus();
  }, []);

  const testAI = async (): Promise<void> => {
    setAiTestLoading(true);
    setAiTestResult(null);
    try {
      const res = await apiPost<{ provider: string; model: string; reply: string }>(
        '/api/admin/ai/test',
        {},
      );
      setAiTestResult({
        ok: true,
        msg: `${res.provider} (${res.model}) replied: "${res.reply}"`,
      });
      loadAIStatus();
    } catch (e) {
      setAiTestResult({
        ok: false,
        msg: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setAiTestLoading(false);
    }
  };

  useEffect(() => {
    apiGet<{ settings: Settings }>('/api/admin/settings')
      .then((r) => setData(r.settings))
      .catch((e: Error) => setErr(e.message));
  }, []);

  const update = (key: string, value: unknown): void => {
    setData((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const save = async (section: string, keys: string[]): Promise<void> => {
    if (!data) return;
    setSaving(true);
    setErr(null);
    setSavedSection(null);
    try {
      for (const key of keys) {
        await apiPut('/api/admin/settings', { key, value: data[key] });
      }
      setSavedSection(section);
      window.setTimeout(() => setSavedSection((s) => (s === section ? null : s)), 2500);
      if (section === 'ai') loadAIStatus();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (): Promise<void> => {
    setTestResult(null);
    try {
      await apiPost('/api/admin/settings/test-email', { to: testEmailTo });
      setTestResult({ ok: true, msg: 'Test email sent. Check inbox and spam.' });
    } catch (e) {
      setTestResult({ ok: false, msg: e instanceof Error ? e.message : String(e) });
    }
  };

  if (err && !data) {
    return (
      <Card title="Admin settings">
        <p style={{ color: 'var(--c-danger)', fontSize: 'var(--text-md)' }}>{err}</p>
        <p style={{ fontSize: 'var(--text-base)', color: 'var(--c-muted)', marginTop: 8 }}>
          You must be signed in as the owner to view this page.
        </p>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card>
        <p style={{ color: 'var(--c-muted)' }}>Loading settings…</p>
      </Card>
    );
  }

  const s = (k: string): string => (data[k] === null ? '' : String(data[k] ?? ''));

  const savedBadge = (section: string): JSX.Element | null =>
    savedSection === section ? (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          color: 'var(--c-success)',
          fontSize: 'var(--text-base)',
          fontWeight: 500,
        }}
      >
        <CheckCircle2 size={14} /> Saved
      </span>
    ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Admin"
        accent="settings"
        sub="Email, eBay, app URL, sign-in access, AI provider — all from one screen. No SSH needed."
      />

      <Card
        title="Email (SMTP)"
        description="Used to send magic-link sign-in messages and alert digests. Brevo / SendGrid / Mailgun all work."
        action={savedBadge('smtp')}
      >
        <Field label="SMTP host" htmlFor="smtp-host">
          <Input
            id="smtp-host"
            value={s('smtp.host')}
            onChange={(e) => update('smtp.host', e.target.value)}
            placeholder="smtp-relay.brevo.com"
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Port" htmlFor="smtp-port">
            <Input
              id="smtp-port"
              type="number"
              value={s('smtp.port') || '587'}
              onChange={(e) => update('smtp.port', Number(e.target.value))}
            />
          </Field>
          <Field label="TLS mode" hint="true=465, false=587" htmlFor="smtp-secure">
            <Select
              id="smtp-secure"
              value={String(data['smtp.secure'] ?? false)}
              onChange={(e) => update('smtp.secure', e.target.value === 'true')}
            >
              <option value="false">STARTTLS (false)</option>
              <option value="true">Implicit TLS (true)</option>
            </Select>
          </Field>
        </div>
        <Field label="SMTP user" htmlFor="smtp-user">
          <Input
            id="smtp-user"
            value={s('smtp.user')}
            onChange={(e) => update('smtp.user', e.target.value)}
            placeholder="abc1234@smtp-brevo.com"
          />
        </Field>
        <Field label="SMTP password" hint="leave as ••• to keep current" htmlFor="smtp-pass">
          <Input
            id="smtp-pass"
            type="password"
            value={s('smtp.password')}
            onChange={(e) => update('smtp.password', e.target.value)}
            placeholder="•••"
          />
        </Field>
        <Field label="From email" hint="must be a verified sender" htmlFor="smtp-from">
          <Input
            id="smtp-from"
            type="email"
            value={s('smtp.fromEmail')}
            onChange={(e) => update('smtp.fromEmail', e.target.value)}
            placeholder="alerts@yourdomain.com"
          />
        </Field>
        <Button
          icon={Mail}
          loading={saving}
          onClick={() =>
            save('smtp', [
              'smtp.host',
              'smtp.port',
              'smtp.secure',
              'smtp.user',
              'smtp.password',
              'smtp.fromEmail',
            ])
          }
        >
          Save SMTP settings
        </Button>
      </Card>

      <Card
        title="Test email"
        description="Send a one-off message using the saved SMTP credentials to confirm delivery."
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <Field label="Send to" htmlFor="test-to">
              <Input
                id="test-to"
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="you@example.com"
              />
            </Field>
          </div>
          <div style={{ marginBottom: 14 }}>
            <Button icon={Send} onClick={sendTest} disabled={!testEmailTo}>
              Send test
            </Button>
          </div>
        </div>
        {testResult && (
          <p
            style={{
              marginTop: 4,
              fontSize: 'var(--text-md)',
              color: testResult.ok ? 'var(--c-success)' : 'var(--c-danger)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {testResult.ok ? <CheckCircle2 size={15} /> : null}
            {testResult.msg}
          </p>
        )}
      </Card>

      <Card
        title="eBay OAuth"
        description={
          'Application keyset from developer.ebay.com. App ID + Cert ID + Dev ID + RuName must all come from the same keyset matching the environment below.'
        }
        action={savedBadge('ebay')}
      >
        <Field label="Environment" htmlFor="ebay-env">
          <Select
            id="ebay-env"
            value={s('ebay.env') || 'production'}
            onChange={(e) => update('ebay.env', e.target.value)}
          >
            <option value="production">Production</option>
            <option value="sandbox">Sandbox</option>
          </Select>
        </Field>
        <Field label="App ID (Client ID)" htmlFor="ebay-appid">
          <Input
            id="ebay-appid"
            value={s('ebay.appId')}
            onChange={(e) => update('ebay.appId', e.target.value)}
            placeholder="username-AppName-PRD-abc123-def4567"
          />
        </Field>
        <Field
          label="Cert ID (Client Secret)"
          hint="leave as ••• to keep current"
          htmlFor="ebay-cert"
        >
          <Input
            id="ebay-cert"
            type="password"
            value={s('ebay.certId')}
            onChange={(e) => update('ebay.certId', e.target.value)}
            placeholder="•••"
          />
        </Field>
        <Field label="Dev ID" htmlFor="ebay-dev">
          <Input
            id="ebay-dev"
            value={s('ebay.devId')}
            onChange={(e) => update('ebay.devId', e.target.value)}
          />
        </Field>
        <Field label="RuName" hint="not a URL — the alias eBay shows in User Tokens" htmlFor="ebay-ruName">
          <Input
            id="ebay-ruName"
            value={s('ebay.ruName')}
            onChange={(e) => update('ebay.ruName', e.target.value)}
            placeholder="YourName-AppName-XYZ-abc123"
          />
        </Field>
        <Button
          icon={ShoppingBag}
          loading={saving}
          onClick={() =>
            save('ebay', ['ebay.env', 'ebay.appId', 'ebay.certId', 'ebay.devId', 'ebay.ruName'])
          }
        >
          Save eBay settings
        </Button>
      </Card>

      <Card
        title="eBay account-deletion compliance"
        description="eBay requires a public webhook for account-deletion notifications before they approve production OAuth. This is the most common reason approvals stall."
        action={savedBadge('compliance')}
      >
        <p style={{ fontSize: 'var(--text-base)', color: 'var(--c-muted)', marginBottom: 12 }}>
          <strong>Setup steps:</strong>
          <br />
          1. Pick a long random string (32+ chars) as your verification token and paste it
          below.
          <br />
          2. In developer.ebay.com → My Account → Application Keysets → "Notifications", set
          the same token and the endpoint URL to{' '}
          <code style={{ background: 'var(--c-divider)', padding: '2px 6px', borderRadius: 4 }}>
            {(s('app.baseUrl') || 'https://your-app-url') + '/api/ebay/account-deletion'}
          </code>
          .
          <br />
          3. Click "Send Test Notification" in the eBay portal — it should return 200 OK.
        </p>
        <Field
          label="Verification token"
          hint="leave as ••• to keep current"
          htmlFor="ebay-deletion-token"
        >
          <Input
            id="ebay-deletion-token"
            type="password"
            value={s('ebay.deletionVerificationToken')}
            onChange={(e) => update('ebay.deletionVerificationToken', e.target.value)}
            placeholder="32+ random characters"
          />
        </Field>
        <Button
          icon={ShieldCheck}
          loading={saving}
          onClick={() => save('compliance', ['ebay.deletionVerificationToken'])}
        >
          Save compliance token
        </Button>
      </Card>

      <Card
        title="Demo mode"
        description="Populate the dashboard with realistic seed data — 2 stores, 20 listings, flagged underperformers, sample notifications. Use this for investor demos when production OAuth isn't approved yet. Idempotent; running again refreshes the dataset."
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            icon={ShoppingBag}
            onClick={async () => {
              try {
                const res = await apiPost<{
                  stores: number;
                  listings: number;
                  flags: number;
                  notifications: number;
                }>('/api/admin/seed-demo', {});
                alert(
                  `Seeded ${res.stores} stores, ${res.listings} listings, ${res.flags} flags, ${res.notifications} notifications. Refresh the dashboard.`,
                );
              } catch (e) {
                alert(e instanceof Error ? e.message : 'Seed failed.');
              }
            }}
          >
            Seed demo data
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (!confirm('Wipe all DEMO_-prefixed rows? Real data stays.')) return;
              try {
                await apiPost('/api/admin/wipe-demo', {});
                alert('Demo data wiped.');
              } catch (e) {
                alert(e instanceof Error ? e.message : 'Wipe failed.');
              }
            }}
          >
            Wipe demo data
          </Button>
        </div>
      </Card>

      <Card
        title="App URL"
        description="Public HTTPS URL where the app is reachable. Used in magic-link emails — must match what users see in the browser."
        action={savedBadge('app')}
      >
        <Field label="Base URL" htmlFor="app-baseurl">
          <Input
            id="app-baseurl"
            value={s('app.baseUrl')}
            onChange={(e) => update('app.baseUrl', e.target.value)}
            placeholder="https://ds01.customerdemourl.com"
          />
        </Field>
        <Button icon={Globe} loading={saving} onClick={() => save('app', ['app.baseUrl'])}>
          Save app URL
        </Button>
      </Card>

      <Card
        title="Sign-in access"
        description="Owner is always allowed even with an empty list. Everyone else on the list signs in with role=staff."
        action={savedBadge('auth')}
      >
        <Field label="Owner email" hint="role=owner on first sign-in" htmlFor="owner-email">
          <Input
            id="owner-email"
            type="email"
            value={s('auth.adminEmail') || 'sysadmin85@gmail.com'}
            onChange={(e) => update('auth.adminEmail', e.target.value)}
          />
        </Field>
        <Field
          label="Allowed sign-in emails"
          hint="comma-separated"
          htmlFor="allowed-emails"
        >
          <Textarea
            id="allowed-emails"
            rows={3}
            value={
              Array.isArray(data['auth.allowedEmails'])
                ? (data['auth.allowedEmails'] as string[]).join(', ')
                : ''
            }
            onChange={(e) =>
              update(
                'auth.allowedEmails',
                e.target.value
                  .split(',')
                  .map((x) => x.trim().toLowerCase())
                  .filter((x) => x.length > 0),
              )
            }
            placeholder="sysadmin85@gmail.com, staff@example.com"
          />
        </Field>
        <Field label="Magic-link expiry (minutes)" hint="1–60" htmlFor="ttl">
          <Input
            id="ttl"
            type="number"
            min={1}
            max={60}
            value={s('auth.magicLinkTtlMin') || '15'}
            onChange={(e) => update('auth.magicLinkTtlMin', Number(e.target.value))}
          />
        </Field>
        <Button
          icon={ShieldCheck}
          loading={saving}
          onClick={() =>
            save('auth', ['auth.adminEmail', 'auth.allowedEmails', 'auth.magicLinkTtlMin'])
          }
        >
          Save sign-in settings
        </Button>
      </Card>

      <Card
        title="AI provider"
        description="Pick which model powers the AI tabs. Google Gemini, Groq, and OpenRouter all have free tiers — links below take you to each provider's key page."
        action={savedBadge('ai')}
      >
        <Field label="Active provider" htmlFor="ai-provider">
          <Select
            id="ai-provider"
            value={s('ai.provider') || 'anthropic'}
            onChange={(e) => update('ai.provider', e.target.value)}
          >
            <option value="anthropic">Anthropic (Claude) — paid</option>
            <option value="openai">OpenAI (GPT-4o-mini) — paid</option>
            <option value="google">Google Gemini — free tier</option>
            <option value="groq">Groq (Llama 3.3) — free</option>
            <option value="openrouter">OpenRouter — has free models</option>
          </Select>
        </Field>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: 12,
            background: 'var(--c-divider)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 'var(--text-md)', color: 'var(--c-text-secondary)' }}>
            <strong>Active now:</strong>{' '}
            {aiStatus?.activeProvider ? (
              <>
                {aiStatus.activeProvider} ({aiStatus.activeModel})
              </>
            ) : (
              <span style={{ color: 'var(--c-muted)' }}>nothing configured</span>
            )}
          </div>
          <Button
            size="sm"
            variant="secondary"
            icon={PlayCircle}
            loading={aiTestLoading}
            disabled={!aiStatus?.activeProvider}
            onClick={testAI}
          >
            Test
          </Button>
        </div>
        {aiTestResult && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: 12,
              borderRadius: 'var(--radius-md)',
              background: aiTestResult.ok ? 'var(--c-success-bg)' : 'var(--c-danger-bg)',
              color: aiTestResult.ok ? 'var(--c-success)' : 'var(--c-danger)',
              fontSize: 'var(--text-md)',
              marginBottom: 12,
            }}
          >
            {aiTestResult.ok ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
            <span>{aiTestResult.msg}</span>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 16,
            marginTop: 4,
            paddingTop: 16,
            borderTop: '1px solid var(--c-divider)',
          }}
        >
          <ProviderSection
            id="anthropic"
            name="Anthropic"
            href="https://console.anthropic.com/settings/keys"
            keyPlaceholder="sk-ant-…"
            keyName="anthropic.apiKey"
            modelName={null}
            data={data}
            update={update}
            keyPresent={Boolean(aiStatus?.keysPresent.anthropic)}
            isActive={aiStatus?.activeProvider === 'anthropic'}
            onKeyTyped={() => update('ai.provider', 'anthropic')}
          />
          <ProviderSection
            id="openai"
            name="OpenAI"
            href="https://platform.openai.com/api-keys"
            keyPlaceholder="sk-…"
            keyName="openai.apiKey"
            modelName="openai.model"
            modelDefault="gpt-4o-mini"
            data={data}
            update={update}
            keyPresent={Boolean(aiStatus?.keysPresent.openai)}
            isActive={aiStatus?.activeProvider === 'openai'}
            onKeyTyped={() => update('ai.provider', 'openai')}
          />
          <ProviderSection
            id="google"
            name="Google Gemini (free tier)"
            href="https://aistudio.google.com/apikey"
            keyPlaceholder="AIza…"
            keyName="google.apiKey"
            modelName="google.model"
            modelDefault="gemini-2.0-flash"
            data={data}
            update={update}
            keyPresent={Boolean(aiStatus?.keysPresent.google)}
            isActive={aiStatus?.activeProvider === 'google'}
            onKeyTyped={() => update('ai.provider', 'google')}
          />
          <ProviderSection
            id="groq"
            name="Groq (free)"
            href="https://console.groq.com/keys"
            keyPlaceholder="gsk_…"
            keyName="groq.apiKey"
            modelName="groq.model"
            modelDefault="llama-3.3-70b-versatile"
            data={data}
            update={update}
            keyPresent={Boolean(aiStatus?.keysPresent.groq)}
            isActive={aiStatus?.activeProvider === 'groq'}
            onKeyTyped={() => update('ai.provider', 'groq')}
          />
          <ProviderSection
            id="openrouter"
            name="OpenRouter (free models available)"
            href="https://openrouter.ai/keys"
            keyPlaceholder="sk-or-…"
            keyName="openrouter.apiKey"
            modelName="openrouter.model"
            modelDefault="meta-llama/llama-3.3-70b-instruct:free"
            data={data}
            update={update}
            keyPresent={Boolean(aiStatus?.keysPresent.openrouter)}
            isActive={aiStatus?.activeProvider === 'openrouter'}
            onKeyTyped={() => update('ai.provider', 'openrouter')}
          />
        </div>

        <Button
          icon={Key}
          loading={saving}
          style={{ marginTop: 16 }}
          onClick={() =>
            save('ai', [
              'ai.provider',
              'anthropic.apiKey',
              'openai.apiKey',
              'openai.model',
              'google.apiKey',
              'google.model',
              'groq.apiKey',
              'groq.model',
              'openrouter.apiKey',
              'openrouter.model',
            ])
          }
        >
          Save AI settings
        </Button>
      </Card>

      {err && (
        <p style={{ color: 'var(--c-danger)', fontSize: 'var(--text-md)' }}>{err}</p>
      )}
    </div>
  );
}
